import { useCallback, useMemo, useState } from 'react'
import { Popover, notification } from 'antd'
import { Tooltip } from 'antd'
import {
  setHandledQuantitySelection,
  removeHandledQuantitySelection,
  setHandledTotalQuantitySelection,
} from '../../../Slices/Kiosk/QuantitiesSlice'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { renderTranslation } from '../../../Helpers/GenericHelpers'
import { Total, AvailableStock, ExtraRecapRow } from '../../../Constants/Configuration'
import NumericInputWithCheck from '../../Generic/NumericInputWithCheck'
import { useAppDispatch, useAppSelector } from '../../../hooks'

function QuantitiesDynamicCell(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const unlockHandling = useAppSelector(state => state.present.quantities.value.unlockHandling)
  const handledQuantitySelection = useAppSelector(state => state.present.quantities.value.handledQuantitySelection)
  const enableAllVariant = useAppSelector(state => state.present.quantities.value.enableAllVariant)
  const quantitiesFilters = useAppSelector(state => state.present.quantities.value.filters)

  const sizeElement = useMemo(
    () => props.storeActiveSizes.filter(columnKey => columnKey.name === props.columnKey),
    [props.columnKey, props.storeActiveSizes],
  )

  const tooltipInfo = () => {
    const additionalInfo = sizeElement.length > 0 && sizeElement[0].additionalInfo ? sizeElement[0].additionalInfo : ''
    return additionalInfo ? (
      <>
        {additionalInfo.map(e => (
          <p>{e}</p>
        ))}
      </>
    ) : null
  }

  const sizeQuantity = props.quantity ? props.quantity : sizeElement.length !== 0 ? sizeElement[0].quantity : ''

  const currentFilterInfo = useMemo(
    () => quantitiesFilters.filter(element => element.value === props.rowKey),
    [quantitiesFilters, props.rowKey],
  )

  const currentStatus = useMemo(
    () => (currentFilterInfo.length !== 0 ? currentFilterInfo[0].status : ''),
    [currentFilterInfo],
  )

  const [isPopoverClicked, setIsPopoverClicked] = useState(false)
  const isTotalColumn = props.columnKey === Total

  const restoreHandled = useCallback(() => {
    //manage array to send up selection
    //always delete availablestock
    if (isTotalColumn) {
      const quantitiesToRemove = props.storeActiveSizes.map(element => {
        return { storeId: props.storeId, rowKey: AvailableStock, sizeId: element.sizeId }
      })

      dispatch(removeHandledQuantitySelection(quantitiesToRemove))
    } else {
      const quantitiesToRemove = [{ storeId: props.storeId, rowKey: AvailableStock, sizeId: props.sizeId }]
      dispatch(removeHandledQuantitySelection(quantitiesToRemove))
    }
  }, [dispatch, props.sizeId, isTotalColumn, props.storeActiveSizes, props.storeId])

  const manageHandled = useCallback(
    amount => {
      //manage array to send up selection
      if (amount && amount > 0) {
        const quantityToAdd = [
          {
            storeId: props.storeId,
            variantId: props.variantId,
            rowKey: props.rowKey,
            columnKey: props.columnKey,
            sizeId: props.sizeId,
            quantity: amount,
          },
        ]
        dispatch(setHandledQuantitySelection(quantityToAdd))
      } else {
        notification.open({
          description: renderTranslation(language, TranslationKeys.quantitiesHandledInputWarning),
          placement: 'topLeft',
          message: '',
        })
      }
    },
    [dispatch, language, props.columnKey, props.rowKey, props.sizeId, props.storeId, props.variantId],
  )

  const manageRecapRow = useCallback(
    amount => {
      if (amount > 0) {
        const quantityToSet = [
          {
            storeId: props.storeId,
            variantId: props.variantId,
            rowKey: ExtraRecapRow,
            columnKey: props.columnKey,
            sizeId: props.sizeId,
            quantity: amount,
          },
        ]
        dispatch(setHandledQuantitySelection(quantityToSet))
      } else {
        restoreHandled()
      }
    },
    [props.columnKey, props.sizeId, props.storeId, props.variantId, restoreHandled, dispatch],
  )

  const manageTotalHandled = useCallback(() => {
    //manage array to send up selection
    const quantitiesToAdd = props.storeActiveSizes.map(element => {
      const quantityToAdd = element.quantity > 0 ? element.quantity : Math.abs(element.quantity)
      return {
        storeId: props.storeId,
        variantId: props.variantId,
        rowKey: props.rowKey,
        columnKey: element.name,
        sizeId: element.sizeId,
        quantity: quantityToAdd,
      }
    })
    dispatch(setHandledTotalQuantitySelection(quantitiesToAdd))
  }, [dispatch, props.rowKey, props.storeActiveSizes, props.storeId, props.variantId])

  const onEnterConfirm = useCallback(
    inputValue => {
      const amount = inputValue
      manageRecapRow(amount)
      setIsPopoverClicked(false)
    },
    [manageRecapRow],
  )

  const onInputConfirm = useCallback(
    inputValue => {
      const amount = inputValue
      manageRecapRow(amount)
      setIsPopoverClicked(false)
    },
    [manageRecapRow],
  )

  const handleClickChange = useCallback(visible => {
    setIsPopoverClicked(visible)
  }, [])

  const renderBasicSizeQuantityBox = useMemo(() => {
    const generalCountBoxCss = sizeQuantity
      ? 'count-box noedit ' +
        props.rowKey.toLowerCase() +
        (isTotalColumn ? ' total' : '') +
        (sizeQuantity < 0 ? ' negative-value ' : '') +
        (enableAllVariant ? ' variant-box ' : '') +
        (currentStatus ? ' ' + currentStatus : '')
      : 'count-box empty-content noedit' + (isTotalColumn ? ' total' : '') + (currentStatus ? ' ' + currentStatus : '')

    if (sizeQuantity) {
      return (
        <Tooltip title={tooltipInfo()}>
          <div className={generalCountBoxCss}>
            <div className='count-box-content noedit'>{sizeQuantity}</div>
          </div>
        </Tooltip>
      )
    }
    //empty content
    return <div className={generalCountBoxCss}></div>
  }, [currentStatus, enableAllVariant, isTotalColumn, props.rowKey, sizeQuantity])

  if (!unlockHandling) {
    //movementation not active
    return renderBasicSizeQuantityBox
  } else {
    if (enableAllVariant) {
      //case variant button is enable
      return renderBasicSizeQuantityBox
    }

    //Recap Row Render When handling is active for columns
    if (props.rowKey === ExtraRecapRow) {
      const currentHandlingSelected =
        props.columnKey !== Total
          ? handledQuantitySelection.filter(
              element => element.storeId === props.storeId && element.columnKey === props.columnKey,
            )
          : handledQuantitySelection.filter(element => element.storeId === props.storeId && element.columnKey !== Total)
      const columnAmount =
        currentHandlingSelected !== 0
          ? currentHandlingSelected.map(element => {
              return element.quantity
            })
          : []
      const columnTotal =
        columnAmount.length !== 0
          ? columnAmount.reduce((previous, current) => {
              return current + previous
            })
          : 0

      if (props.columnKey !== Total) {
        return (
          <Popover
            id='popover-check'
            title={renderTranslation(language, TranslationKeys.quantitiesInputHandleNumber)}
            open={isPopoverClicked}
            content={
              <NumericInputWithCheck
                columnTotal={columnTotal}
                onEnterConfirm={onEnterConfirm}
                onInputConfirm={onInputConfirm}
              />
            }
            trigger='click'
            onOpenChange={handleClickChange}
          >
            <div className={'count-box recap-box cursor-pointer' + (columnTotal > 0 ? ' active' : '')}>
              <div className={'count-box-content noedit'}>{columnTotal > 0 ? columnTotal : ''}</div>
            </div>
          </Popover>
        )
      } else {
        return (
          <div className={'count-box recap-box total noedit' + (columnTotal > 0 ? ' active' : '')}>
            <div className={'count-box-content total-box noedit'}>{columnTotal > 0 ? columnTotal : ''}</div>
          </div>
        )
      }
    }

    //movementation active, force movementation if quantity < 0
    //movementation render
    if (props.rowKey === AvailableStock) {
      const currentMovementation = isTotalColumn
        ? props.allValueSelected
          ? props.allValueSelected
          : false
        : handledQuantitySelection.filter(
            element =>
              element.storeId === props.storeId &&
              element.rowKey === props.rowKey &&
              element.columnKey === props.columnKey,
          )
      const isCellActive = isTotalColumn ? currentMovementation : currentMovementation.length !== 0
      const activeCountBoxCss =
        'count-box handled noedit ' +
        props.rowKey.toLowerCase() +
        (isCellActive ? ' active ' : '') +
        (isTotalColumn ? ' total' : '')
      const movementationValue = sizeQuantity ? sizeQuantity : 0

      if (isCellActive) {
        if (props.columnKey !== Total) {
          //current selection amount
          const renderAmount = currentMovementation[0].quantity
          const renderrestAmount = movementationValue - renderAmount
          return (
            <div
              className={activeCountBoxCss + (renderrestAmount < 0 ? ' negative-value noedit' : '')}
              onClick={e => {
                manageHandled(1)
              }}
            >
              <div className='count-diff noedit float-val'>{movementationValue}</div>
              <div className='count-value noedit'>{renderrestAmount}</div>
            </div>
          )
        } else {
          return (
            <div
              className={activeCountBoxCss}
              onClick={e => {
                restoreHandled()
              }}
            >
              <div className='count-value noedit'>{movementationValue}</div>
            </div>
          )
        }
      } else {
        if (props.columnKey !== Total) {
          return (
            <div
              className={activeCountBoxCss}
              onClick={e => {
                manageHandled(1)
              }}
            >
              <div className='count-value noedit'>{movementationValue}</div>
            </div>
          )
        } else {
          return (
            <div
              className={activeCountBoxCss}
              onClick={e => {
                if (movementationValue === 0) {
                  return
                }
                manageTotalHandled()
              }}
            >
              <div className='count-box-content noedit'>{movementationValue}</div>
            </div>
          )
        }
      }
    }

    return renderBasicSizeQuantityBox
  }
}

export default QuantitiesDynamicCell
